/**
 * A material's thickness, in inches.
 *
 * `material_library.thickness` is a free-text TEXT column that was display-only
 * until foam started being priced by volume, and it has been typed every way a
 * person writes a thickness: `2`, `.75`, `3/4`, `3/4"`, `1-1/2`. This is the
 * only place that text becomes a number, so every caller agrees on what
 * `1-1/2` means.
 *
 * Anything it cannot read comes back `null`, never a guess. A wrong thickness
 * moves a board-foot rate silently, so the caller warns and charges nothing
 * instead.
 */

/** A trailing unit, with the divisor that turns it into inches. */
const UNIT_SUFFIX = /(mm|cm|inches|inch|in|"|″)$/;
const DIVISOR_BY_UNIT: Readonly<Record<string, number>> = { mm: 25.4, cm: 2.54 };

/** `3/4`, or a whole number and a fraction joined by a hyphen or a space. */
const FRACTION = /^(?:(\d+)[\s-]+)?(\d+)\/(\d+)$/;
/** `2`, `0.25`, `.75`. No sign: a thickness is never negative. */
const DECIMAL = /^(?:\d+(?:\.\d+)?|\.\d+)$/;

export function parseThicknessInches(
  raw: string | number | null | undefined,
): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null;
  if (typeof raw !== 'string') return null;

  let text = raw.trim().toLowerCase();
  let divisor = 1;
  const unit = text.match(UNIT_SUFFIX);
  if (unit) {
    divisor = DIVISOR_BY_UNIT[unit[1]] ?? 1;
    text = text.slice(0, -unit[1].length).trim();
  }

  let value: number;
  const fraction = text.match(FRACTION);
  if (fraction) {
    const denominator = Number(fraction[3]);
    if (denominator === 0) return null;
    value = Number(fraction[1] ?? 0) + Number(fraction[2]) / denominator;
  } else if (DECIMAL.test(text)) {
    value = Number(text);
  } else {
    return null;
  }

  value /= divisor;
  return Number.isFinite(value) && value > 0 ? value : null;
}
